import { useCallback } from "react";
import { copyToClipboard } from "@/lib/clipboard";
import type { HashKeyActions } from "@/features/auth/types/hashKeys.types";
import { toast } from "sonner";
import { useNavigate } from "@tanstack/react-router";
import { useBatchDeleteHashKeys } from "@/features/auth/api/hash.queries";

/**
 * Custom hook for hash keys actions
 */
export const useHashKeysActions = (
  onSelect: (keyId: string) => void,
  onSelectAll: () => void,
  selectedKeys: string[],
  onClearSelection: () => void,
): HashKeyActions => {
  const navigate = useNavigate();
  const batchDeleteMutation = useBatchDeleteHashKeys();

  const handleEdit = useCallback(
    (keyId: string) => {
      navigate({
        to: "/manager/subscription-management/edit-user/$hashKeyId",
        params: { hashKeyId: keyId },
        search: { hashKeyId: keyId },
      });
    },
    [navigate],
  );

  const handleDelete = useCallback(
    (keyId: string) => {
      console.log("Delete hash key:", keyId);
      batchDeleteMutation.mutate({ hash_key_ids: [keyId] });
      onClearSelection();
      toast.success("Hash key deleted");
    },
    [batchDeleteMutation, onClearSelection],
  );

  const handleCopyHash = useCallback((hashKey: string) => {
    copyToClipboard(hashKey);
    toast.success("Hash key copied to clipboard");
  }, []);

  const handleAddNew = useCallback(() => {
    navigate({
      to: "/manager/subscription-management/add-new-user",
    });
  }, [navigate]);

  const handleBulkDelete = useCallback(() => {
    console.log("handleBulkDelete", selectedKeys);
    if (selectedKeys.length === 0) {
      toast.error("Please select hash keys to delete");
      return;
    }

    batchDeleteMutation.mutate(
      { hash_key_ids: selectedKeys },
      {
        onSuccess: () => {
          onClearSelection(); // Clear selection after successful deletion
          toast.success(
            `Successfully deactivated ${selectedKeys.length} hash key(s)`,
          );
          navigate({ to: "/manager/subscription-management" });
        },
      },
    );
  }, [selectedKeys, batchDeleteMutation, onClearSelection, navigate]);

  return {
    onEdit: handleEdit,
    onDelete: handleDelete,
    onCopyHash: handleCopyHash,
    onSelect,
    onSelectAll,
    onAddNew: handleAddNew,
    onBulkDelete: handleBulkDelete,
    isBulkDeleting: batchDeleteMutation.isPending,
  };
};
